// src/pages/AdminCategoriesPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import Loader from "../layouts/components/Loader";
import toast from "react-hot-toast";
import "./AdminCategoriesPage.css";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

export default function AdminCategoriesPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    image: null,
  });

  // ===== Helpers =====
  const getToken = () =>
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";

  const resolveImageUrl = (cat) => {
    if (!cat) return null;
    // Prefer explicit URLs if present
    if (typeof cat.image_url === "string" && cat.image_url) return cat.image_url;
    if (typeof cat.image === "string" && cat.image) return cat.image;
    // If API returns object-like image { url, path }
    if (cat.image && typeof cat.image === "object") {
      if (cat.image.url) return cat.image.url;
      if (cat.image.path)
        return `${API_BASE.replace("/api", "")}/storage/${cat.image.path}`;
    }
    return null;
  };

  useEffect(() => {
    const token = getToken();
    const userRole = localStorage.getItem("user_role");

    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }
    fetchCategories(token);
  }, [navigate]);

  const fetchCategories = async (token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Accept several shapes (array, {data:[]}, {categories:[]})
        const list = Array.isArray(data?.categories)
          ? data.categories
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];
        setCategories(list);
      } else {
        toast.error("Failed to fetch categories");
        setCategories([]);
      }
    } catch (error) {
      toast.error("Error fetching categories");
      console.error("Error:", error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();
    setIsSubmitting(true);

    // Guard: name required (trim to avoid spaces being accepted)
    const nameTrimmed = (formData.name || "").trim();
    if (!nameTrimmed) {
      toast.error("Category name is required");
      return;
    }

    const fd = new FormData();
    fd.append("name", nameTrimmed);
    if (formData.image) {
      fd.append("image", formData.image);
    }

    const isEdit = Boolean(editingCategory);
    const url = isEdit
      ? `${API_BASE}/categories/${editingCategory.id}`
      : `${API_BASE}/categories`;

    // Spoof PUT for multipart compatibility
    if (isEdit) fd.append("_method", "PUT");

    try {
      const response = await fetch(url, {
        method: "POST", // always POST when sending FormData (PUT is spoofed)
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          // Don't set Content-Type manually with FormData
        },
        body: fd,
      });

      if (response.ok) {
        toast.success(
          isEdit ? "Category updated successfully" : "Category created successfully"
        );
        setShowModal(false);
        setEditingCategory(null);
        setFormData({ name: "", image: null });
        fetchCategories(token);
      } else {
        const errorData = await response.json().catch(() => ({}));
        // Surface first validation error if available
        if (errorData?.errors && typeof errorData.errors === "object") {
          const firstKey = Object.keys(errorData.errors)[0];
          const firstMsg = errorData.errors[firstKey]?.[0];
          toast.error(firstMsg || errorData.message || "Failed to save category");
        } else {
          toast.error(errorData.message || "Failed to save category");
        }
        console.error("Save error detail:", errorData);
      }
    } catch (error) {
      toast.error("Error saving category");
      console.error("Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category?.name || "",
      image: null, // don't preload file input
    });
    setShowModal(true);
  };

  const handleDelete = async (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    setDeletingCategory(category);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingCategory) return;
    
    setIsDeleting(true);
    const token = getToken();

    try {
      const response = await fetch(`${API_BASE}/categories/${deletingCategory.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        toast.success("Category deleted successfully");
        setShowDeleteModal(false);
        setDeletingCategory(null);
        fetchCategories(token);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.message || "Failed to delete category");
      }
    } catch (error) {
      toast.error("Error deleting category");
      console.error("Error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingCategory(null);
    setIsDeleting(false);
  };

  const openModal = () => {
    setEditingCategory(null);
    setFormData({ name: "", image: null });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({ name: "", image: null });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-categories">
          <div className="loading">Loading categories...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-categories">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Categories Management</h1>
            <p>Manage product categories</p>
          </div>
          <div className="admin-actions">
            <button className="btn-primary" onClick={openModal}>
              <svg viewBox="0 0 24 24">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" />
              </svg>
              Add New Category
            </button>
          </div>
        </div>

        {/* Categories Table */}
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 ? (
                <tr>
                  <td colSpan="4" className="empty-state">
                    No categories found
                  </td>
                </tr>
              ) : (
                categories.map((category) => {
                  const imgUrl = resolveImageUrl(category);
                  return (
                    <tr key={category.id}>
                      <td>
                        <div className="category-image">
                          {imgUrl ? (
                            <img
                              src={imgUrl}
                              alt={category.name}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                if (e.currentTarget.nextSibling) {
                                  e.currentTarget.nextSibling.style.display = "flex";
                                }
                              }}
                            />
                          ) : null}
                          <div
                            className="image-placeholder"
                            style={{ display: imgUrl ? "none" : "flex" }}
                          >
                            <svg viewBox="0 0 24 24">
                              <path
                                d="M4 16l4.586-4.586a2 2 0 0 1 2.828 0L16 16m-2-2l1.586-1.586a2 2 0 0 1 2.828 0L20 14m-6-6h.01M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                            </svg>
                          </div>
                        </div>
                      </td>
                      <td>{category.name}</td>
                      <td>
                        {category.created_at
                          ? new Date(category.created_at).toLocaleDateString()
                          : "-"}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(category)}
                            title="Edit"
                          >
                            <svg viewBox="0 0 24 24">
                              <path
                                d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <path
                                d="M18.5 2.5a2 2 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                            </svg>
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(category.id)}
                            title="Delete"
                          >
                            <svg viewBox="0 0 24 24">
                              <path
                                d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <line
                                x1="10"
                                y1="11"
                                x2="10"
                                y2="17"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <line
                                x1="14"
                                y1="11"
                                x2="14"
                                y2="17"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingCategory ? "Edit Category" : "Add New Category"}</h2>
                <button className="modal-close" onClick={closeModal}>
                  <svg viewBox="0 0 24 24">
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-group">
                  <label htmlFor="name">Category Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter category name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="image">Category Image</label>
                  <input
                    type="file"
                    id="image"
                    name="image"
                    onChange={handleInputChange}
                    accept="image/*"
                  />
                  {editingCategory && resolveImageUrl(editingCategory) && (
                    <div className="current-image">
                      <p>Current image:</p>
                      <img
                        src={resolveImageUrl(editingCategory)}
                        alt="Current"
                      />
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader size={16} strokeWidth={2} />
                        {editingCategory ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      editingCategory ? "Update Category" : "Create Category"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title="Delete Category"
          message="Are you sure you want to delete this category?"
          itemName={deletingCategory ? deletingCategory.name : ""}
          isLoading={isDeleting}
        />
      </div>
    </AdminLayout>
  );
}
